import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Sidebar.css";

import {
  FaBars,
  FaTimes,
  FaHome,
  FaClipboardList,
  FaTrophy,
  FaChartBar,
  FaUsers,
  FaUserCircle,
  FaSignOutAlt,
  FaBuilding,
  FaShieldAlt,
  FaUserFriends,
} from "react-icons/fa";

import api from "@api";

function Sidebar() {
  const {
    isAuthenticated,
    logout,
    loading,
    user,
    profileRefreshKey,
  } = useAuth();

  const [hasProfile, setHasProfile] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingClaimsCount, setPendingClaimsCount] = useState(0);

  const isSuper = user?.role === "super_admin";
  const hasOrg = !!user?.organization_id;
  const hasListing = !!user?.directoryLeagueId;

  const isFreePlan =
    user?.plan === "free" ||
    user?.plan === "directory_free";

  const isTrialPlan = user?.plan === "trial";
  const isListingPlusPlan = user?.plan === "listing_plus";
  const isLeagueToolsPlan = user?.plan === "league_tools";
  const isBrandedSitePlan = user?.plan === "branded_site";
  const isCustomDomainPlan = user?.plan === "custom_domain";

  const trialEndsAt = user?.trial_ends_at
    ? new Date(user.trial_ends_at)
    : null;

  const now = new Date();
  const hasTrialDate = !!trialEndsAt;

  const isTrialActive =
    isTrialPlan &&
    trialEndsAt &&
    trialEndsAt > now;

  const isTrialExpired =
    trialEndsAt &&
    trialEndsAt <= now;

  const hasUsedTrial =
    !!user?.has_used_trial ||
    hasTrialDate ||
    isTrialPlan;

  const canUseLeagueTools =
    isTrialActive ||
    isLeagueToolsPlan ||
    isBrandedSitePlan ||
    isCustomDomainPlan;

  const canSeeWebsite =
    hasOrg &&
    (isBrandedSitePlan || isCustomDomainPlan);

  const canSeePlayers =
    isTrialActive ||
    isLeagueToolsPlan ||
    isBrandedSitePlan ||
    isCustomDomainPlan;

  const canSeeTeams =
    isTrialActive ||
    isLeagueToolsPlan ||
    isBrandedSitePlan ||
    isCustomDomainPlan;

  const canShowTrialCTA =
    hasListing &&
    !hasUsedTrial &&
    (isFreePlan || isListingPlusPlan);

  const canShowUpgradeCTA =
    hasListing &&
    !canUseLeagueTools &&
    (
      hasUsedTrial ||
      isTrialExpired ||
      isListingPlusPlan
    );

  const shouldShowStart =
    isAuthenticated &&
    !isSuper &&
    !hasOrg &&
    !hasProfile;

  const trialDaysLeft = trialEndsAt
    ? Math.max(
      0,
      Math.ceil(
        (trialEndsAt - new Date()) /
        (1000 * 60 * 60 * 24)
      )
    )
    : 0;

  // Super admin pending claims
  useEffect(() => {
    const fetchPendingClaims = async () => {
      if (user?.role !== "super_admin") return;

      try {
        const res = await api.get(
          "/api/directory-league-claims",
          {
            withCredentials: true,
          }
        );

        const pending = (res.data || []).filter(
          (claim) => claim.status === "pending"
        );

        setPendingClaimsCount(pending.length);
      } catch (err) {
        console.error(
          "Failed to fetch claims count",
          err
        );
      }
    };

    fetchPendingClaims();
  }, [user]);

  // Personal social profile
  useEffect(() => {
    const fetchMyProfile = async () => {
      if (!isAuthenticated || isSuper) return;

      try {
        const res = await api.get(
          "/api/user-profiles-v2/me",
          {
            withCredentials: true,
          }
        );

        setHasProfile(res.data.hasProfile);
        setProfileId(
          res.data.profile?.id || null
        );
      } catch (error) {
        console.error(
          "Failed to fetch user profile:",
          error
        );
      }
    };

    fetchMyProfile();
  }, [
    isAuthenticated,
    isSuper,
    profileRefreshKey,
  ]);

  const toggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const handleStartTrial = async () => {
    try {
      if (!user?.organization_id) {
        alert(
          "No organization found for this listing."
        );
        return;
      }

      await api.post(
        `/api/organizations/${user.organization_id}/start-trial`,
        {},
        {
          withCredentials: true,
        }
      );

      window.location.href = "/dashboard/seasons";
    } catch (err) {
      console.error(
        "Failed to start trial:",
        err
      );

      alert(
        err.response?.data?.message ||
        "Failed to start trial."
      );
    }
  };

  const handleLogout = async () => {
    try {
      const fallbackRole = user?.role;
      const fallbackDomain = user?.domain;
      const fallbackSlug = user?.slug;
      const fallbackPlan = user?.plan;

      const fallbackDirectoryLeagueId =
        user?.directoryLeagueId;

      const response = await logout();

      if (fallbackRole === "super_admin") {
        window.location.href = "/";
        return;
      }

      const domain =
        response?.domain ||
        fallbackDomain;

      const slug =
        response?.slug ||
        fallbackSlug;

      const plan =
        response?.plan ||
        fallbackPlan;

      const directoryLeagueId =
        response?.directoryLeagueId ||
        fallbackDirectoryLeagueId;

      const isLocalhost =
        window.location.hostname === "localhost";

      if (
        plan === "custom_domain" &&
        domain &&
        !isLocalhost
      ) {
        window.location.href =
          `https://${domain}`;

        return;
      }

      if (
        (
          plan === "branded_site" ||
          plan === "custom_domain"
        ) &&
        slug
      ) {
        const base = isLocalhost
          ? `http://localhost:5173/${slug}`
          : `https://sportinghip.com/${slug}`;

        window.location.href = base;
        return;
      }

      if (directoryLeagueId) {
        window.location.href =
          `/leagues/${directoryLeagueId}`;

        return;
      }

      window.location.href = "/";
    } catch (error) {
      console.error(
        "Logout Error:",
        error
      );

      window.location.href = "/";
    }
  };

  if (loading) return null;
  if (!isAuthenticated) return null;

  return (
    <>
      {!isOpen && (
        <button
          className="sh-hamburger"
          onClick={toggleSidebar}
        >
          <FaBars />
        </button>
      )}

      <aside
        className={`sh-sidebar ${isOpen ? "open" : ""
          }`}
      >
        {isOpen && (
          <button
            className="sh-close"
            onClick={toggleSidebar}
          >
            <FaTimes />
          </button>
        )}

        <div className="sh-sidebar-inner">

          {/* Brand */}
          <div className="sh-brand">
            <div className="sh-brand-mark">
              <FaShieldAlt />
            </div>

            <div className="sh-brand-text">
              Sporting<span>Hip</span>
            </div>
          </div>

          <nav className="sh-nav">

            {/* Start */}
            {shouldShowStart && (
              <NavLink
                to="/dashboard/start-page"
                onClick={closeSidebar}
                className="sh-main-link"
              >
                <FaHome />
                <span>Start</span>
              </NavLink>
            )}

            {/* Personal */}
            {!isSuper && hasProfile && (
              <div className="sh-section">
                <div className="sh-section-label">
                  Personal
                </div>

                <NavLink
                  to={`/profile/${profileId}`}
                  onClick={closeSidebar}
                  className="sh-link"
                >
                  <FaUserCircle />
                  <span>My Profile</span>
                </NavLink>
              </div>
            )}

            {/* Directory */}
            {!isSuper && hasListing && (
              <div className="sh-section">
                <div className="sh-section-label">
                  Directory
                </div>

                <NavLink
                  to={`/dashboard/directory/manage/${user.directoryLeagueId}`}
                  onClick={closeSidebar}
                  className="sh-link"
                >
                  <FaClipboardList />
                  <span>Manage Listing</span>
                </NavLink>
              </div>
            )}

            {/* League Management */}
            {!isSuper &&
              (
                canUseLeagueTools ||
                canShowTrialCTA ||
                canShowUpgradeCTA
              ) && (
                <div className="sh-section">
                  <div className="sh-section-label">
                    League Management
                  </div>

                  {/* Trial CTA */}
                  {canShowTrialCTA && (
                    <button
                      type="button"
                      onClick={handleStartTrial}
                      className="sh-link sh-trial-link sh-link-button"
                    >
                      <FaTrophy />

                      <span>
                        Try League Management
                      </span>

                      <span className="sh-pill">
                        Free Trial
                      </span>
                    </button>
                  )}

                  {/* Upgrade CTA */}
                  {canShowUpgradeCTA && (
                    <NavLink
                      to="/pricing"
                      onClick={closeSidebar}
                      className="sh-link sh-trial-link"
                    >
                      <FaTrophy />

                      <span>
                        Upgrade League Tools
                      </span>

                      <span className="sh-pill">
                        Upgrade
                      </span>
                    </NavLink>
                  )}

                  {/* League Tools */}
                  {canUseLeagueTools && (
                    <>
                      {/* Seasons */}
                      <NavLink
                        to="/dashboard/seasons"
                        onClick={closeSidebar}
                        className="sh-link"
                      >
                        <FaTrophy />

                        <span className="sh-link-text">
                          <span>Seasons</span>

                          {isTrialActive && (
                            <small className="sh-trial-inline">
                              Free trial ·{" "}
                              {trialDaysLeft}{" "}
                              {trialDaysLeft === 1
                                ? "day"
                                : "days"}{" "}
                              left
                            </small>
                          )}
                        </span>
                      </NavLink>

                      {/* Players */}
                      {canSeePlayers && (
                        <NavLink
                          to="/dashboard/playerListAuth"
                          onClick={closeSidebar}
                          className="sh-link"
                        >
                          <FaUsers />
                          <span>Players</span>
                        </NavLink>
                      )}

                      {/* Teams */}
                      {canSeeTeams && (
                        <NavLink
                          to="/dashboard/teamListAuth"
                          onClick={closeSidebar}
                          className="sh-link"
                        >
                          <FaUserFriends />
                          <span>Teams</span>
                        </NavLink>
                      )}

                      {/* Stats */}
                      <NavLink
                        to="/dashboard/stats"
                        onClick={closeSidebar}
                        className="sh-link"
                      >
                        <FaChartBar />
                        <span>Stats</span>
                      </NavLink>

                    <NavLink
                      to={`/dashboard/organizations/${user.organization_id}`}
                      onClick={closeSidebar}
                      className="sh-link"
                    >
                      <FaBuilding />
                      <span>Edit Website</span>
                    </NavLink>
                    </>
                  )}
                </div>
              )}

            {/* Super Admin */}
            {isSuper && (
              <div className="sh-section">
                <div className="sh-section-label">
                  Admin
                </div>

                <NavLink
                  to="/dashboard/admin/organizations"
                  onClick={closeSidebar}
                  className="sh-link"
                >
                  <FaBuilding />
                  <span>Organizations</span>
                </NavLink>

                <NavLink
                  to="/dashboard/admin/directory-leagues"
                  onClick={closeSidebar}
                  className="sh-link"
                >
                  <FaClipboardList />

                  <span>
                    Directory Leagues
                  </span>
                </NavLink>

                <NavLink
                  to="/dashboard/admin/directory-insights"
                  onClick={closeSidebar}
                  className="sh-link"
                >
                  <FaChartBar />

                  <span>
                    Directory Insights
                  </span>
                </NavLink>

                <NavLink
                  to="/dashboard/admin/directory-claims"
                  onClick={closeSidebar}
                  className="sh-link"
                >
                  <FaClipboardList />

                  <span>
                    Directory Claims
                  </span>

                  {pendingClaimsCount > 0 && (
                    <span className="sh-badge">
                      {pendingClaimsCount}
                    </span>
                  )}
                </NavLink>
              </div>
            )}

            {/* Settings */}
            <div className="sh-section">
              <div className="sh-section-label">
                Settings
              </div>

              <NavLink
                to="/dashboard/userSettings"
                onClick={closeSidebar}
                className="sh-link"
              >
                <FaUserCircle />
                <span> Account Settings </span>
              </NavLink>

              {isSuper && (
                <NavLink
                  to="/dashboard/playerListAuth"
                  onClick={closeSidebar}
                  className="sh-link"
                >
                  <FaUsers />
                  <span>All Players</span>
                </NavLink>
              )}
            </div>

          </nav>

          {/* Logout */}
          <div className="sh-sidebar-bottom">
            <button
              className="sh-logout-btn"
              onClick={handleLogout}
            >
              <FaSignOutAlt />
              <span>Log Out</span>
            </button>
          </div>

        </div>
      </aside>
    </>
  );
}

export default Sidebar;